import { useEffect, useState } from "react";
import { X } from "lucide-react";

const DISMISS_KEY = "queonda.ondi.bubble.dismissed_at";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24 h
const IDLE_SHOW_DELAY_MS = 8000;
const WIDTH_ESTIMATE = 128;
const HEIGHT_ESTIMATE = 46;

function recentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!at || Number.isNaN(at)) return false;
    return Date.now() - at < DISMISS_TTL_MS;
  } catch (_e) {
    return false;
  }
}

/**
 * OndiBubble — small glassmorphic dialog bubble ("¿Qué deseas?") that appears
 * near the mascot when the user is idle. Dismissable and throttled 24 h in
 * localStorage. Auto-hides when the panel/tour/driving overlay opens.
 *
 * Positioning: bubble is placed above the mascot; horizontally shifted so it
 * never overflows the viewport (points right when mascot is on left half of
 * screen, points left when mascot is on the right half).
 */
export function OndiBubble({ anchor, characterWidth, characterHeight, suppressed }) {
  const [show, setShow] = useState(false);

  // Schedule appearance after IDLE_SHOW_DELAY_MS if not recently dismissed
  useEffect(() => {
    if (suppressed) {
      setShow(false);
      return undefined;
    }
    if (recentlyDismissed()) return undefined;
    const t = setTimeout(() => setShow(true), IDLE_SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [suppressed]);

  // Hide the bubble whenever another ONDI surface takes over
  useEffect(() => {
    if (suppressed) setShow(false);
  }, [suppressed]);

  if (!show || suppressed) return null;

  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const midX = anchor.x + characterWidth / 2;
  const alignRight = midX < vw / 2; // mascot on left half → bubble to the right
  const gapX = 10;
  const bubbleX = alignRight
    ? Math.min(anchor.x + characterWidth + gapX, vw - WIDTH_ESTIMATE - 12)
    : Math.max(12, anchor.x - WIDTH_ESTIMATE - gapX);
  const bubbleY = Math.max(12, anchor.y + characterHeight * 0.12 - HEIGHT_ESTIMATE / 2);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_e) {
      /* ignore */
    }
    setShow(false);
  };

  return (
    <div
      className="q-glass border border-white/15 rounded-2xl shadow-2xl px-3 py-2 flex items-center gap-2 select-none"
      style={{
        position: "fixed",
        left: bubbleX,
        top: bubbleY,
        zIndex: 54,
        boxShadow:
          "0 6px 22px rgba(0,0,0,0.45), 0 0 18px var(--accent-soft)",
        animation: "ondi-bubble-in 260ms ease-out both",
      }}
      data-testid="ondi-bubble"
      role="status"
      aria-live="polite"
    >
      {/* Little pointer arrow */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "50%",
          [alignRight ? "left" : "right"]: -6,
          transform: "translateY(-50%) rotate(45deg)",
          width: 10,
          height: 10,
          background: "inherit",
          borderLeft: alignRight ? "1px solid rgba(255,255,255,0.15)" : "none",
          borderBottom: alignRight ? "1px solid rgba(255,255,255,0.15)" : "none",
          borderTop: !alignRight ? "1px solid rgba(255,255,255,0.15)" : "none",
          borderRight: !alignRight ? "1px solid rgba(255,255,255,0.15)" : "none",
          backgroundColor: "rgba(15,23,42,0.72)",
          backdropFilter: "blur(10px)",
        }}
      />
      <span className="q-accent-text text-sm font-medium leading-tight" data-testid="ondi-bubble-text">
        ¿Qué deseas?
      </span>
      <button
        type="button"
        onClick={dismiss}
        className="text-slate-400 hover:text-slate-100 p-0.5"
        aria-label="Descartar mensaje de ONDI"
        data-testid="ondi-bubble-dismiss"
        data-ondi-no-drag=""
      >
        <X size={13} />
      </button>
      <style>{`
        @keyframes ondi-bubble-in {
          from { opacity: 0; transform: translateY(4px) scale(0.94); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
